import { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { canStillOrderToday, nextBusinessDay } from '@/lib/cutoff';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('gc3h_cart');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Legacy single delivery date - kept for backwards compatibility
  const [deliveryDate, setDeliveryDate] = useState(() => {
    const saved = localStorage.getItem('gc3h_delivery_date');
    return saved || '';
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  // Shared cart-line identity helpers: a line is unique per item + delivery date + chosen sides
  const sidesKeyOf = (item) => item.selected_sides?.map(s => s.side_id).sort().join('_') || 'no_sides';
  const keyFor = (item, date = null) => `${item.item_id}_${date || item.delivery_date || 'default'}_${sidesKeyOf(item)}`;

  useEffect(() => {
    localStorage.setItem('gc3h_cart', JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem('gc3h_delivery_date', deliveryDate);
  }, [deliveryDate]);

  // Auto-bump stale delivery dates: once the 9AM cut-off (+10 min grace) closes,
  // items still set to today (or a past date) move to the next business day automatically
  useEffect(() => {
    const pad = (n) => String(n).padStart(2, '0');
    const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const bumpStaleDates = () => {
      const now = new Date();
      const todayStr = toStr(now);
      const isStale = (d) => d && (d < todayStr || (d === todayStr && !canStillOrderToday(now)));
      const staleCount = items.filter(i => isStale(i.delivery_date)).length;
      if (staleCount === 0) return;
      const next = nextBusinessDay(now);
      const nextStr = toStr(next);
      setItems(prev => {
        const stale = prev.filter(i => isStale(i.delivery_date));
        const result = prev.filter(i => !isStale(i.delivery_date)).map(i => ({ ...i }));
        // Merge each bumped line into an existing same-item/same-date/same-sides line (sum quantities)
        stale.forEach(b => {
          const bumpedKey = keyFor(b, nextStr);
          const existing = result.find(k => keyFor(k) === bumpedKey);
          if (existing) {
            existing.quantity += b.quantity;
          } else {
            result.push({ ...b, delivery_date: nextStr, cart_key: bumpedKey });
          }
        });
        return result;
      });
      const label = next.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'short' });
      toast.info(
        `Today's 9:00 AM cut-off has passed — ${staleCount} cart item${staleCount > 1 ? 's were' : ' was'} moved to ${label}.`,
        { duration: 7000 }
      );
    };
    bumpStaleDates();
    const id = setInterval(bumpStaleDates, 60000);
    return () => clearInterval(id);
  }, [items, isCartOpen]);

  const addItem = (item) => {
    setItems(prev => {
      // Create a unique key that includes sides
      const sidesKey = item.selected_sides?.map(s => s.side_id).sort().join('_') || 'no_sides';
      const existingKey = `${item.item_id}_${item.delivery_date || 'default'}_${sidesKey}`;
      
      // Find existing item with same ID, delivery date, AND same sides
      const existing = prev.find(i => {
        const iSidesKey = i.selected_sides?.map(s => s.side_id).sort().join('_') || 'no_sides';
        return i.item_id === item.item_id && 
          (i.delivery_date || 'default') === (item.delivery_date || 'default') &&
          iSidesKey === sidesKey;
      });
      
      if (existing) {
        return prev.map(i => {
          const iSidesKey = i.selected_sides?.map(s => s.side_id).sort().join('_') || 'no_sides';
          if (i.item_id === item.item_id && 
              (i.delivery_date || 'default') === (item.delivery_date || 'default') &&
              iSidesKey === sidesKey) {
            return { ...i, quantity: i.quantity + 1 };
          }
          return i;
        });
      }
      
      // Add unique key for React rendering
      return [...prev, { 
        ...item, 
        quantity: 1,
        cart_key: existingKey
      }];
    });
  };

  const removeItem = (itemId, deliveryDate = null, cartKey = null) => {
    setItems(prev => prev.filter(i => {
      if (cartKey) {
        return (i.cart_key || keyFor(i)) !== cartKey;
      }
      if (deliveryDate) {
        return !(i.item_id === itemId && i.delivery_date === deliveryDate);
      }
      return i.item_id !== itemId;
    }));
  };

  const updateQuantity = (itemId, quantity, deliveryDate = null, cartKey = null) => {
    if (quantity <= 0) {
      removeItem(itemId, deliveryDate, cartKey);
      return;
    }
    setItems(prev => prev.map(i => {
      if (cartKey) {
        return (i.cart_key || keyFor(i)) === cartKey ? { ...i, quantity } : i;
      }
      if (deliveryDate) {
        return (i.item_id === itemId && i.delivery_date === deliveryDate) 
          ? { ...i, quantity } 
          : i;
      }
      return i.item_id === itemId ? { ...i, quantity } : i;
    }));
  };

  const clearCart = () => {
    setItems([]);
    setDeliveryDate('');
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(prev => !prev);

  const total = items.reduce((sum, item) => {
    const itemTotal = item.price * item.quantity;
    const sidesTotal = (item.sides_total || 0) * item.quantity;
    return sum + itemTotal + sidesTotal;
  }, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Group items by delivery date for checkout display
  const getItemsByDeliveryDate = () => {
    const grouped = {};
    items.forEach(item => {
      const date = item.delivery_date || 'unspecified';
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(item);
    });
    return grouped;
  };

  // Get unique delivery dates in cart
  const getDeliveryDates = () => {
    const dates = [...new Set(items.map(i => i.delivery_date).filter(Boolean))];
    return dates.sort();
  };

  // Update delivery date for a specific item — merges into an existing same-item/same-sides line on the new date
  const updateItemDeliveryDate = (itemId, oldDeliveryDate, newDeliveryDate) => {
    setItems(prev => {
      const moving = prev.filter(i => i.item_id === itemId && i.delivery_date === oldDeliveryDate);
      if (moving.length === 0) return prev;
      const result = prev.filter(i => !(i.item_id === itemId && i.delivery_date === oldDeliveryDate)).map(i => ({ ...i }));
      moving.forEach(m => {
        const movedKey = keyFor(m, newDeliveryDate);
        const existing = result.find(k => keyFor(k) === movedKey);
        if (existing) {
          existing.quantity += m.quantity;
        } else {
          result.push({ ...m, delivery_date: newDeliveryDate, cart_key: movedKey });
        }
      });
      return result;
    });
  };

  // Add multiple items at once (for reorder feature)
  const addMultipleItems = (newItems) => {
    setItems(prev => {
      const combined = [...prev];
      newItems.forEach(item => {
        const sidesKey = item.selected_sides?.map(s => s.side_id).sort().join('_') || 'no_sides';
        const cartKey = `${item.item_id}_${item.delivery_date || 'default'}_${sidesKey}`;
        combined.push({
          ...item,
          quantity: item.quantity || 1,
          cart_key: cartKey,
        });
      });
      return combined;
    });
  };

  // Set all items to the same delivery date (merging duplicate lines)
  const setAllDeliveryDates = (newDate) => {
    setItems(prev => {
      const merged = [];
      prev.forEach(i => {
        const k = keyFor(i, newDate);
        const existing = merged.find(m => m.cart_key === k);
        if (existing) {
          existing.quantity += i.quantity;
        } else {
          merged.push({ ...i, delivery_date: newDate, cart_key: k });
        }
      });
      return merged;
    });
  };

  const value = {
    items,
    deliveryDate,
    setDeliveryDate,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    total,
    itemCount,
    isEmpty: items.length === 0,
    isCartOpen,
    openCart,
    closeCart,
    toggleCart,
    getItemsByDeliveryDate,
    getDeliveryDates,
    updateItemDeliveryDate,
    setAllDeliveryDates,
    addMultipleItems
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
